/**
 * ReviewForm
 * Form for submitting or editing reviews
 * Protected actions with auth validation
 */

import { Button } from '@shared/components/ui/button'
import { Input } from '@shared/components/ui/input'
import { Label } from '@shared/components/ui/label'
import { StarRating } from '@shared/components/ui/star-rating'
import { Textarea } from '@shared/components/ui/textarea'
import { useProtectedAction } from '@shared/hooks'
import { Calendar, Loader2, MessageSquare } from 'lucide-react'
import { useState } from 'react'
import { useCreateReview } from '../hooks/useReviews'
import { ApiClientError, isSelfReviewRejectionError } from '@shared/lib/api/client'
import { logger } from '@shared/lib/utils/logger'

// ============================================================================
// Types
// ============================================================================

interface ReviewFormProps {
  professionalId: string
  /**
   * When the viewer owns the professional profile being reviewed, the form
   * fields are replaced by an explanatory message (the backend would reject
   * the submission with a 422 anyway).
   */
  isSelfReview?: boolean
  onSuccess?: () => void
  onCancel?: () => void
}

// ============================================================================
// Component
// ============================================================================

export function ReviewForm({
  professionalId,
  isSelfReview = false,
  onSuccess,
  onCancel,
}: ReviewFormProps) {
  const [rating, setRating] = useState(5)
  const [comment, setComment] = useState('')
  const [jobDate, setJobDate] = useState(
    new Date().toISOString().split('T')[0]
  )
  const [submitError, setSubmitError] = useState<string | null>(null)

  const createReview = useCreateReview()
  const { executeProtected, isValidating } = useProtectedAction()

  const isSubmitting = createReview.isPending || isValidating

  const handleDateClick = (e: React.MouseEvent<HTMLInputElement>) => {
    try {
      const input = e.currentTarget as HTMLInputElement & { showPicker?: () => void };
      if (input.showPicker) {
        input.showPicker();
      }
    } catch {
      // Fallback: just focus the input
      e.currentTarget.focus();
    }
  };
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitError(null)

    try {
      await executeProtected(async () => {
        await createReview.mutateAsync({
          professionalId,
          rating,
          comment: comment.trim() || undefined,
          jobDate: jobDate || undefined,
        })
      })
      onSuccess?.()
    } catch (error) {
      // Show the backend's friendly (already Spanish) message for API errors,
      // e.g. "No podés dejar comentarios a tu propio perfil profesional."
      setSubmitError(
        error instanceof ApiClientError
          ? error.message
          : 'Error al publicar la reseña. Por favor, intenta de nuevo.'
      )
      // Only report genuinely unexpected errors to Sentry; the self-review
      // rejection (422) is normal user feedback, not a bug. Other 422
      // validation errors still get reported.
      if (!isSelfReviewRejectionError(error)) {
        logger.error('Failed to submit review:', error)
      }
    }
  }

  // Self-review gate: you can't review your own professional profile, so show
  // an explanatory message instead of the form fields. The 422-handling in
  // handleSubmit / useReviews remains as a defense-in-depth safety net.
  if (isSelfReview) {
    return (
      <div className="text-center py-12">
        <MessageSquare className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
        <p className="text-muted-foreground">
          No puedes dejarte una reseña a tí mismo, pero puedes invitar a amigos y clientes anteriores a hacerlo 😉, compartiles tu perfil.
        </p>
      </div>
    )
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="space-y-6">
      {/* Rating */}
      <div className="space-y-2">
        <Label>Calificación *</Label>
        <div className="flex items-center gap-4">
          <StarRating
            rating={rating}
            onChange={setRating}
            size="lg"
            showValue={false}
          />
          <span className="text-2xl font-semibold">{rating}.0</span>
        </div>
        <p className="text-sm text-muted-foreground">
          Selecciona de 1 a 5 estrellas
        </p>
      </div>

      {/* Comment */}
      <div className="space-y-2 bg-">
        <Label htmlFor="comment">Comentario (Opcional)</Label>
        <Textarea
          id="comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Cuéntanos sobre tu experiencia con este profesional..."
          rows={5}
          maxLength={500}
          disabled={isSubmitting}
          className='resize-none bg-input border-2 border-border rounded-md'
        />
        <p className="text-xs text-muted-foreground text-right">
          {comment.length}/500 caracteres
        </p>
      </div>

      {/* Job Date */}
      <div className="relative space-y-2">
        <Label htmlFor="jobDate">Fecha del Trabajo (Opcional)</Label>
        <Input
          id="jobDate"
          type="date"
          value={jobDate}
          onChange={(e) => setJobDate(e.target.value)}
          max={new Date().toISOString().split('T')[0]} // Can't select future dates
          disabled={isSubmitting}
          onClick={handleDateClick}
          className='bg-input border-2 border-border rounded-md [&::-webkit-calendar-picker-indicator]:hidden'
        >
        </Input>
        <Calendar className="absolute right-3 top-3 transform translate-y-5 w-5 h-5 text-muted-foreground pointer-events-none" />
      </div>

      {/* Form Actions */}
      <div className="flex gap-4 pt-4">
        <Button type="submit" disabled={isSubmitting} className="flex-1">
          {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {isSubmitting
            ? 'Enviando...'
            : 'Publicar Reseña'}
        </Button>
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
        )}
      </div>

      {/* Success/Error Messages */}
      {createReview.isSuccess && (
        <p className="text-sm text-green-600">
          ¡Reseña publicada correctamente!
        </p>
      )}
      {submitError && (
        <p className="text-sm text-destructive">
          {submitError}
        </p>
      )}
    </form>
  )
}
